import template from './import_holiday.html';
import tr from '../../lib/default-translator.js';

class AdminImportHolidayViewModel
{
	constructor (dialog)
	{
		this.dialog = dialog;
		let today = new Date();
		let oneyear = new Date().setFullYear(new Date().getFullYear() + 1);
		this.countries = ko.observableArray();
		this.imported_holidays = ko.observableArray();
		this.selected_country = ko.observable();
		this.from_date = ko.observable(new Date(today).toJSON().slice(0, 10));
		this.to_date = ko.observable(new Date(oneyear).toJSON().slice(0, 10));
		this.upload_input = ko.observable();
		this.loader = ko.observable(false);
		this.existing_holidays = ko.observableArray([]); // Track existing holidays
		this.duplicate_action = ko.observable('skip'); // 'skip', 'overwrite', 'ask'
		
		//bind functions
		this.import_holidays = this.import_holidays.bind(this);
		this.importCSV = this.importCSV.bind(this);
		this.load_existing_holidays = this.load_existing_holidays.bind(this);
		
		// Load existing holidays when initialized
		this.load_existing_holidays();
	}

	
	normalize_holiday_name(name)
	{
		if (!name) return '';
		
		let variations = {
			'rosh hashana': 'rosh hashanah',
			'rosh hashannah': 'rosh hashanah',
			'rosh hashanah': 'rosh hashanah',
			'yom kippur': 'yom kippur',
			'yom kipur': 'yom kippur',
			'passover': 'passover',
			'pesach': 'passover',
			'sukkot': 'sukkot',
			'succot': 'sukkot',
			'shavot': 'shavot',
			'shavuot': 'shavot'
		};
		
		let normalized = name.toLowerCase().trim().replace(/[^\w\s]/g, '').replace(/\s+/g, ' ');
		
		return variations[normalized] || normalized;
	}

	// Calculate string similarity
	calculate_similarity(str1, str2)
	{
		let len1 = str1.length;
		let len2 = str2.length;
		
		if (len1 === 0) return len2;
		if (len2 === 0) return len1;
		
		let matrix = Array(len2 + 1).fill(null).map(() => Array(len1 + 1).fill(null));
		
		for (let i = 0; i <= len1; i++) matrix[0][i] = i;
		for (let j = 0; j <= len2; j++) matrix[j][0] = j;
		
		for (let j = 1; j <= len2; j++)
		{
			for (let i = 1; i <= len1; i++)
			{
				let cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
				matrix[j][i] = Math.min(
					matrix[j - 1][i] + 1,
					matrix[j][i - 1] + 1,
					matrix[j - 1][i - 1] + cost
				);
			}
		}
		
		let max_length = Math.max(len1, len2);
		return (max_length - matrix[len2][len1]) / max_length;
	}


	is_duplicate_holiday(new_holiday, _existing_holiday)
	{
		// Same date and holiday type required
		if (new_holiday.holiday_date !== _existing_holiday.holiday_date || new_holiday.holiday_type !== _existing_holiday.holiday_type) return false;
		
		let name1 = this.normalize_holiday_name(new_holiday.description);
		let name2 = this.normalize_holiday_name(_existing_holiday.description);
		
		// Exact match
		if (name1 === name2) return true;
		
		// Fuzzy match
		let similarity = this.calculate_similarity(name1, name2);
		return similarity > 0.8; // 80% similarity threshold
	}

	// Load existing holidays for duplicate checking
	async load_existing_holidays()
	{
		try
		{
			let res = await Grape.fetches.getJSON('/holiday/list_holidays', {
				holiday_type: this.dialog.bindings.holiday_type,
				from_date: this.from_date(),
				to_date: this.to_date()
			});
			this.existing_holidays(res.holidays || []);
		}
		catch (error) {
			console.error('Error loading existing holidays:', error);
		}
	}

	// Find duplicates in a list of holidays
	find_duplicates(holidays_to_import)
	{
		let duplicates = [];
		let _existing_holidays = this.existing_holidays();
		
		holidays_to_import.forEach(new_holiday => {
			let duplicate = _existing_holidays.find(existing => 
				this.is_duplicate_holiday(new_holiday, existing)
			);
			
			if (duplicate)
			{
				duplicates.push({
					new: new_holiday,
					existing: duplicate
				});
			}
		});
		
		return duplicates;
	}

	async import_holidays()
	{
		if (this.from_date() == null || this.from_date() == undefined || this.from_date() == '')
		{
			Grape.alerts.alert({type: 'warning', title: tr('Missing from date!'), message: tr('Please choose a date to import from')});
			return;
		}
		if (this.to_date() == null || this.to_date() == undefined || this.to_date() == '')
		{
			Grape.alerts.alert({type: 'warning', title: tr('Missing to date!'), message: tr('Please choose a date to import to')});
			return;
		}
		if (this.from_date() > this.to_date())
		{
			Grape.alerts.alert({type: 'warning', title: tr('Invalid date formats'), message: tr('To date cannot be before from date!')});
			return;
		}

		let country_id = this.selected_country();
		let timeMin = new Date(this.from_date()).toISOString();
		let timeMax = new Date(this.to_date()).toISOString();
		
		this.loader(true);
		
		try
		{
			// Get holidays from Google
			let res = await Grape.fetches.getJSON('/holiday/get_country_holidays', { 
				timeMin: timeMin, 
				timeMax: timeMax, 
				country_id: country_id 
			});
		
			if (res.error)
			{
				Grape.alerts.alert({ type: 'error', message: tr('Could not retrieve holidays.'), title: tr('Error') });
				this.loader(false);
				return; 
			}

			let google_holidays = res.holidays || [];
			if (google_holidays.length === 0)
			{
				Grape.alerts.alert({ type: 'info', message: tr('No holidays found for the selected period.'), title: tr('Info') });
				this.loader(false);
				return;
			}

			// Prepare holidays for import
			let holidays_to_import = google_holidays.map(google_holiday => ({
				holiday_date: google_holiday.start.date,
				description: google_holiday.summary,
				holiday_type: this.dialog.bindings.holiday_type,
				command: 'modify'
			}));

			// Check for duplicates
			let duplicates = this.find_duplicates(holidays_to_import);
			
			if (duplicates.length > 0)
			{
				let duplicates_list = duplicates.map(d => 
					`• ${d.new.description} (${d.new.holiday_date}) - similar to existing: ${d.existing.description}`
				).join('\n');
				
				let response = await Grape.alerts.confirm({
					type: 'warning',
					title: tr('Duplicate Holidays Found'),
					message: tr('Found ') + duplicates.length + tr(' potential duplicate holidays:') + '\n\n' + duplicates_list + '\n\n' + tr('How would you like to proceed?'),
					accept_text: tr('Import All (Skip Duplicates)'),
					cancel_text: tr('Cancel Import'),
					custom_buttons: [
						{ text: tr('Import All (Overwrite Duplicates)'), value: 'overwrite' }
					]
				});
				
				if (!response)
				{
					this.loader(false);
					return;
				}
				
				this.duplicate_action(response === 'overwrite' ? 'overwrite' : 'skip');
			}

			// Import holidays
			await this.process_holiday_import(holidays_to_import, duplicates);
			
		}
		catch (error)
		{
			console.error('Import error:', error);
			Grape.alerts.alert({ type: 'error', message: tr('An error occurred during import.'), title: tr('Error') });
		}
		finally
		{
			this.loader(false);
		}
	}

	async process_holiday_import(holidays_to_import, duplicates)
	{
		let successful = 0;
		let skipped = 0;
		let errors = [];
		
		for (const holiday of holidays_to_import)
		{
			try
			{
				// Check if this holiday is a duplicate
				let is_duplicate = duplicates.some(d => 
					d.new.holiday_date === holiday.holiday_date && 
					d.new.description === holiday.description
				);
				
				if (is_duplicate && this.duplicate_action() === 'skip')
				{
					skipped++;
					continue;
				}
				
				// Save holiday
				let res = await Grape.fetches.postJSON('/admin/save_holiday', holiday);
				
				if (res.status === 'OK') 
					successful++;
				else 
					errors.push(`${holiday.description} (${holiday.holiday_date}): ${res.message || 'Unknown error'}`);
			}
			catch (error)
			{
				errors.push(`${holiday.description} (${holiday.holiday_date}): ${error.message}`);
			}
		}
		
		// Show results
		let message = tr('Successfully imported ') + successful + tr(' holidays.');
		if (skipped > 0) 
			message += '\n' + tr('Skipped ') + skipped + tr(' duplicate holidays.');
		
		if (errors.length > 0) 
			message += '\n' + tr('Failed to import ') + errors.length + tr(' holidays.');
		
		await Grape.alerts.alert({ 
			type: successful > 0 ? 'success' : 'warning', 
			title: tr('Import Complete'), 
			message: message
		});
		
		this.dialog.close(true);
	}

	async importCSV(v, e)
	{
		let f = e.currentTarget.files[0];
		if (!f)
		{
			Grape.alerts.alert({type: 'danger', message: tr('Failed to load file.'), title: tr('Load failed:')});
			return;
		}
		
		if (!f.name.match('\.csv$'))
		{
			let msg = f.name + ' ' + tr('is not a valid CSV file.');
			Grape.alerts.alert({type: 'danger', message: msg, title: tr('Load failed:')});
			return;
		}
		
		let r = new FileReader();
		r.onload = (e) => {
			try
			{
				let holidays = [];
				let required_headers = ['description', 'holiday_date'];
				let lines = e.target.result.replaceAll('"', '').split(/\r?\n/);
				let headers = lines[0].split(/\s*,\s*/);
				let diff = _.difference(required_headers, headers); // Fixed syntax
				
				let field = (row, header) => {
					return row[headers.indexOf(header)].trim();
				};
				
				if (diff.length != 0)
				{
					Grape.alerts.alert({type: 'danger', message: tr('Headers missing:') + ' ' + diff, title: tr('Import failed:')});
					return;
				}
				
				for (let i = 1; i < lines.length; i++)
				{
					let line = lines[i];
					if (line != '')
					{
						let obj = line.split(',');
						let holiday = { holiday_type: this.dialog.bindings.holiday_type };
						for (let h = 0; h < headers.length; h++)
						{
							holiday[headers[h]] = field(obj, headers[h]);
						}
						holidays.push(holiday);
					}
				}
				
				// Validate dates
				for (let i in holidays)
				{
					if (!(moment(holidays[i].holiday_date, 'YYYY-MM-DD', true).isValid()))
					{
						Grape.alerts.alert({type: 'error', title: tr('Invalid CSV file'), message: tr('CSV contains invalid date entries')});
						this.clearCSVInputs();
						holidays = [];
						break;
					}
				}
				
				this.imported_holidays(holidays);
			}
			catch (error)
			{
				Grape.alerts.alert({type: 'error', title: tr('CSV Parse Error'), message: error.message});
			}
		};
		r.readAsText(f);
	}

	async importCSVClick()
	{
		this.loader(true);
		
		try
		{
			let holidays_to_import = this.imported_holidays();
			
			// Check for duplicates
			let duplicates = this.find_duplicates(holidays_to_import);
			
			if (duplicates.length > 0)
			{
				let response = await Grape.alerts.confirm({
					type: 'warning',
					title: tr('Duplicate Holidays Found'),
					message: tr('Found ') + duplicates.length + tr(' potential duplicates. Skip duplicates?'),
					accept_text: tr('Skip Duplicates'),
					cancel_text: tr('Cancel')
				});
				
				if (!response)
				{
					this.loader(false);
					return;
				}
				
				this.duplicate_action('skip');
			}
			
			let successful = 0;
			let skipped = 0;
			let errors = [];
			
			for (let holiday of holidays_to_import)
			{
				try
				{
					// Check if duplicate
					let is_duplicate = duplicates.some(d => 
						d.new.holiday_date === holiday.holiday_date && 
						this.normalize_holiday_name(d.new.description) === this.normalize_holiday_name(holiday.description)
					);
					
					if (is_duplicate && this.duplicate_action() === 'skip')
					{
						skipped++;
						continue;
					}
					
					holiday.command = 'modify';
					let res = await Grape.fetches.postJSON('/admin/save_holiday', holiday);
					
					if (res.status == 'OK') 
						successful++;
					else 
						errors.push(`${holiday.description}: ${res.message || 'Unknown error'}`);
				}
				catch (err)
				{
					errors.push(`${holiday.description}: ${err.message}`);
				}
			}
			
			let message = `Successfully imported ${successful} of ${holidays_to_import.length} holidays.`;
			if (skipped > 0) 
				message += ` Skipped ${skipped} duplicates.`;

			if (errors.length > 0)
				message += ` ${errors.length} failed.`;
			
			Grape.alerts.alert({
				type: successful > 0 ? 'success' : 'warning', 
				title: tr('Import complete'), 
				message: message
			});
			
		}
		catch (error)
		{
			Grape.alerts.alert({type: 'error', title: tr('Import Error'), message: error.message});
		}
		finally
		{
			this.loader(false);
		}
	}

	close()
	{
		this.dialog.close(true);
	}

	clearCSVInputs()
	{
		$('#batchuploadform').get(0).reset();
		this.imported_holidays([]);
	}
}

class AdminImportHolidayPage
{
	constructor (bindings)
	{
		this.viewModel = new AdminImportHolidayViewModel(this);
		this.bindings = bindings;
	}

	async init()
	{
		localStorage.setItem('holiday_type_selected', this.bindings.holiday_type);
		try
		{
			let options = {
				table: 'country_settings',
				limit: 1000
			}
			let res = await Grape.fetches.getJSON('/api/record', options);
			if (res.status == 'OK')
				this.viewModel.countries(res.records);
		}
		catch (error)
		{
			Grape.alerts.alert({ type: 'error', title: 'Error', message: error.message});
			console.error(error);
		}
	}

	clearCSVInputs()
	{
		$('#batchuploadform').get(0).reset();
		this.viewModel.imported_holidays([]);
	}
}

export default {
	name: 'AdminImportHoliday',
	dialog_class: AdminImportHolidayPage,
	template: template,
	template_filename: 'translation/dialogs/import_holiday',
	provider: 'ps'
};